/**
 * Health bars and ammunition pips.
 *
 * Shown only when they say something: a bar appears when a unit is hurt and
 * disappears when it is repaired or replaced, and ammunition pips appear only
 * on things that carry a magazine. A permanent bar over every unit is a second
 * army drawn on top of the first, and at this zoom it is the bars you end up
 * reading instead of the battle.
 *
 * One instanced mesh, one draw call, unlit and un-fogged — a health bar is a
 * readout, not an object in the world, so it does not dim with the ground it
 * stands over. It is still hidden for anything the player cannot see, because a
 * bar floating in the fog would give away exactly what the fog is hiding.
 */

import {
  Color,
  InstancedMesh,
  Matrix4,
  MeshBasicMaterial,
  PlaneGeometry,
  Quaternion,
  Scene,
  Vector3,
} from 'three'
import { Entities, EF } from '../sim/entities.js'
import { defById } from '../sim/data/entities.js'
import type { TransformBuffer } from './interp.js'
import type { Palette } from '../forge/palette.js'

const MAX_BARS = 2048

/** Bar size in world cells. */
const BAR_WIDTH = 0.9
const BAR_HEIGHT = 0.16
/**
 * How far above the unit's own height the bar floats. Tracks `UNIT_SCALE`: a
 * tank's turret roof stands at about 1.25 cells now, and a bar drawn through it
 * reads as damage rather than as a gauge.
 */
const UNIT_LIFT = 1.3
const STRUCTURE_LIFT = 1.9

/** Rank chevron size, and how far under the unit they sit. */
const CHEVRON_WIDTH = 0.13
const CHEVRON_HEIGHT = 0.07
const CHEVRON_GAP = 0.04
const CHEVRON_DROP = 0.72

/** Ammunition pip size, and the gap between pips. */
const PIP_WIDTH = 0.19
const PIP_HEIGHT = 0.12
const PIP_GAP = 0.06

/**
 * Every colour comes from the generated palette.
 *
 * Hard-coding them was quicker and put six colours on screen that exist nowhere
 * else in the game — which the colour-discipline gate noticed, and which is
 * also just how a HUD ends up looking bolted on.
 */
function ramp(palette: Palette, name: string, step: number): Color {
  const entry = palette.accentRamps[name] ?? palette.neutralRamp
  const [r, g, b] = palette.rgb(entry.indices[Math.min(step, entry.indices.length - 1)])
  return new Color(r / 255, g / 255, b / 255)
}

export class Bars {
  private readonly mesh: InstancedMesh
  private readonly scratchMatrix = new Matrix4()
  private readonly scratchPos = new Vector3()
  private readonly scratchScale = new Vector3()
  private readonly scratchColor = new Color()
  /** Orientation that faces the fixed camera. Set once the basis is known. */
  private readonly facing = new Quaternion()

  /** Ground height under a world position, so a bar clears the terrain it is on. */
  groundAt: (x: number, z: number) => number = () => 0
  /** Whether the local player may see an entity at all. */
  visible: (slot: number) => boolean = () => true

  private readonly full: Color
  private readonly hurt: Color
  private readonly critical: Color
  private readonly backing: Color
  private readonly pipFull: Color
  private readonly pipSpent: Color
  private readonly repairMark: Color
  private readonly rankMark: Color

  constructor(scene: Scene, palette: Palette) {
    this.full = ramp(palette, 'status-good', 1)
    this.hurt = ramp(palette, 'status-warn', 1)
    this.critical = ramp(palette, 'status-bad', 1)
    this.backing = ramp(palette, 'neutral', 0)
    this.pipFull = ramp(palette, 'helios', 1)
    this.pipSpent = ramp(palette, 'neutral', 1)
    this.repairMark = ramp(palette, 'status-good', 2)
    this.rankMark = ramp(palette, 'status-warn', 1)
    // No `vertexColors`: a plane has no colour attribute, and asking the
    // material for one it does not have renders every bar black. The tint comes
    // from the instance colour, which is a separate path.
    const material = new MeshBasicMaterial({
      transparent: true,
      // Drawn over the world: a bar occluded by the building it belongs to is
      // worse than useless, because it reads as a bar that is not there.
      depthTest: false,
      depthWrite: false,
    })
    this.mesh = new InstancedMesh(new PlaneGeometry(1, 1), material, MAX_BARS)
    this.mesh.frustumCulled = false
    this.mesh.count = 0
    this.mesh.renderOrder = 10
    scene.add(this.mesh)
  }

  /** Face the camera. The camera never rotates, so this is set once. */
  setFacing(cameraQuaternion: Quaternion): void {
    this.facing.copy(cameraQuaternion)
  }

  sync(entities: Entities, transforms: TransformBuffer, alpha: number): void {
    let n = 0
    const limit = entities.limit
    for (let slot = 0; slot < limit && n + 12 < MAX_BARS; slot++) {
      if (!entities.isAlive(slot)) continue
      if (entities.has(slot, EF.Building)) continue
      if (!this.visible(slot)) continue

      const maxHp = entities.maxHp[slot]
      const hp = entities.hp[slot]
      const x = transforms.x(slot, alpha)
      const z = transforms.y(slot, alpha)
      const structure = entities.has(slot, EF.Structure)
      const air = entities.has(slot, EF.Air)
      let lift = this.groundAt(x, z) + (structure ? STRUCTURE_LIFT : UNIT_LIFT)
      if (air) lift += 1.5

      if (maxHp > 0 && hp < maxHp) {
        const fraction = Math.max(0, hp / maxHp)
        n += this.bar(n, x, lift, z, fraction, structure)
        // A repairing structure gets a marker over its bar. Repair is a toggle
        // that quietly drains credits, so whether it is running has to be
        // legible from the battlefield and not only from the sidebar.
        if (entities.has(slot, EF.Repairing)) {
          n += this.pips(n, x, lift + BAR_HEIGHT + 0.1, z, 1, 1, this.repairMark)
        }
      }

      // Ammunition, for anything that carries a magazine.
      const def = defById(entities.kind[slot])
      const rounds = def?.ordnance ?? 0
      if (rounds > 0) {
        n += this.pips(n, x, lift + BAR_HEIGHT + 0.1, z, entities.ammo[slot], rounds)
      }

      // Rank. Always drawn, unlike the health bar, because a veteran is
      // something you want to find in a crowd *before* it is in trouble —
      // knowing which tank to pull out of a losing fight is the entire point of
      // the mechanic, and a marker that only appears once it is damaged tells
      // you too late.
      const rank = entities.rank[slot]
      if (rank > 0) {
        n += this.chevrons(n, x, lift - CHEVRON_DROP, z, rank)
      }
    }

    this.mesh.count = n
    this.mesh.instanceMatrix.needsUpdate = true
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true
  }

  dispose(): void {
    this.mesh.geometry.dispose()
    ;(this.mesh.material as MeshBasicMaterial).dispose()
    this.mesh.dispose()
  }

  /** A backing plate and a fill. Returns how many instances were written. */
  private bar(
    at: number,
    x: number,
    y: number,
    z: number,
    fraction: number,
    structure: boolean,
  ): number {
    const width = structure ? BAR_WIDTH * 1.6 : BAR_WIDTH
    this.quad(at, x, y, z, width, BAR_HEIGHT, this.backing)

    // Green until it matters, amber when it does, red when it is nearly over.
    // Interpolating through the middle instead reads as one long fade and the
    // moment a unit becomes worth retreating is exactly what the colour is for.
    const colour = fraction > 0.6 ? this.full : fraction > 0.3 ? this.hurt : this.critical
    const inset = width * 0.9 * fraction
    this.quad(at + 1, x - (width * 0.9 - inset) / 2, y, z, inset, BAR_HEIGHT * 0.62, colour, 0.002)
    return 2
  }

  /** One pip per round, dark once spent. */
  private pips(
    at: number,
    x: number,
    y: number,
    z: number,
    left: number,
    total: number,
    lit = this.pipFull,
  ): number {
    const span = total * PIP_WIDTH + (total - 1) * PIP_GAP
    for (let i = 0; i < total; i++) {
      const offset = -span / 2 + i * (PIP_WIDTH + PIP_GAP) + PIP_WIDTH / 2
      this.quad(at + i, x + offset, y, z, PIP_WIDTH, PIP_HEIGHT, i < left ? lit : this.pipSpent)
    }
    return total
  }

  /**
   * One small mark per rank, stacked under the unit.
   *
   * Below rather than above: the space above is already spoken for by health
   * bars and ammunition pips, and stacking a third readout there turns a
   * skirmish into a wall of floating furniture.
   */
  private chevrons(at: number, x: number, y: number, z: number, rank: number): number {
    const span = rank * CHEVRON_WIDTH + (rank - 1) * CHEVRON_GAP
    for (let i = 0; i < rank; i++) {
      const offset = -span / 2 + i * (CHEVRON_WIDTH + CHEVRON_GAP) + CHEVRON_WIDTH / 2
      this.quad(at + i, x + offset, y, z, CHEVRON_WIDTH, CHEVRON_HEIGHT, this.rankMark)
    }
    return rank
  }

  private quad(
    index: number,
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    colour: Color,
    depthBias = 0,
  ): void {
    this.scratchPos.set(x, y + depthBias, z)
    this.scratchScale.set(width, height, 1)
    this.scratchMatrix.compose(this.scratchPos, this.facing, this.scratchScale)
    this.mesh.setMatrixAt(index, this.scratchMatrix)
    this.mesh.setColorAt(index, this.scratchColor.copy(colour))
  }
}
